import React, { useState, useEffect } from 'react';
import { getContentRights, updateContentRights } from '../../services/api';

// Utility function to extract error message safely
const getErrorMessage = (error) => {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    if (error.message) return error.message;
    if (error.details && typeof error.details === 'string') return error.details;
    if (error.details && Array.isArray(error.details) && error.details.length > 0) {
      return error.details[0].detail || error.details[0].title || 'API Error';
    }
    return JSON.stringify(error);
  }
  return 'Unknown error';
};

function ContentRights({ app, appDetails, onUpdate }) {
  const [selectedDeclaration, setSelectedDeclaration] = useState('');
  const [currentDeclaration, setCurrentDeclaration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load current content rights declaration from Apple API
  useEffect(() => {
    const loadContentRights = async () => {
      if (!app || !app.id) return;
      
      setLoadingInitial(true);
      setError('');
      
      try {
        const response = await getContentRights(app.id);
        if (response.data.success) {
          const declaration = response.data.contentRightsDeclaration;
          setCurrentDeclaration(declaration);
          setSelectedDeclaration(declaration || '');
        } else {
          setError(getErrorMessage(response.data.error) || 'Failed to load content rights declaration');
        }
      } catch (err) {
        setError(getErrorMessage(err.response?.data?.error || err) || 'Failed to load content rights declaration');
      } finally {
        setLoadingInitial(false);
      }
    };

    loadContentRights();
  }, [app]);

  const handleUpdateContentRights = async () => {
    if (!selectedDeclaration) {
      setError('Please select a content rights declaration');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await updateContentRights(app.id, selectedDeclaration);
      if (response.data.success) {
        setSuccess('✅ Content rights declaration updated successfully');
        setCurrentDeclaration(selectedDeclaration);
        onUpdate && onUpdate();
      } else {
        setError(getErrorMessage(response.data.error) || 'Failed to update content rights declaration');
      }
    } catch (err) {
      setError(getErrorMessage(err.response?.data?.error || err) || 'Failed to update content rights declaration');
    } finally {
      setLoading(false);
    }
  };

  const getDeclarationLabel = (declaration) => {
    if (declaration === 'DOES_NOT_USE_THIRD_PARTY_CONTENT') {
      return '✅ Does Not Use Third-Party Content';
    } else if (declaration === 'USES_THIRD_PARTY_CONTENT') {
      return '⚠️ Uses Third-Party Content';
    }
    return 'Not Set';
  };

  return (
    <div className="content-rights-section">
      <div className="section-header">
        <h2>⚖️ Content Rights Declaration</h2>
        <p>
          Declare whether your app uses third-party content. This is required information
          for App Store submissions.
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {loadingInitial && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading content rights declaration...</span>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Updating content rights declaration...</span>
        </div>
      )}

      <div className="content-rights-content">
        {/* Current Status */}
        <div className="current-status-section">
          <h3>Current Status</h3>
          {currentDeclaration ? (
            <div className={`status-badge status-${currentDeclaration.toLowerCase().replace(/_/g, '-')}`}>
              {getDeclarationLabel(currentDeclaration)}
            </div>
          ) : (
            <div className="status-badge status-not-set">
              ⚠️ Not Set - Declaration Required
            </div>
          )}
        </div>

        {/* Declaration Options */}
        <div className="declaration-options-section">
          <h3>Select Content Rights Declaration</h3>
          <p>Choose the option that best describes your app's use of third-party content:</p>

          <div className="radio-options">
            <label className="radio-option">
              <input
                type="radio"
                name="contentRights"
                value="DOES_NOT_USE_THIRD_PARTY_CONTENT"
                checked={selectedDeclaration === 'DOES_NOT_USE_THIRD_PARTY_CONTENT'}
                onChange={(e) => setSelectedDeclaration(e.target.value)}
                disabled={loading || loadingInitial}
              />
              <div className="radio-content">
                <div className="radio-title">
                  ✅ Does Not Use Third-Party Content
                </div>
                <div className="radio-description">
                  Your app does not use any content from third parties. All content in your app
                  is either created by you or you have full rights to use it.
                </div>
              </div>
            </label>

            <label className="radio-option">
              <input
                type="radio"
                name="contentRights"
                value="USES_THIRD_PARTY_CONTENT"
                checked={selectedDeclaration === 'USES_THIRD_PARTY_CONTENT'}
                onChange={(e) => setSelectedDeclaration(e.target.value)}
                disabled={loading || loadingInitial}
              />
              <div className="radio-content">
                <div className="radio-title">
                  ⚠️ Uses Third-Party Content
                </div>
                <div className="radio-description">
                  Your app uses content from third parties (e.g., images, music, videos, text)
                  and you have obtained the necessary rights or licenses to use this content.
                </div>
              </div>
            </label>
          </div>

          <div className="action-buttons">
            <button
              className="btn btn-primary"
              onClick={handleUpdateContentRights}
              disabled={loading || loadingInitial || !selectedDeclaration || selectedDeclaration === currentDeclaration}
            >
              {loading ? 'Updating...' : 'Update Content Rights Declaration'}
            </button>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section">
          <h3>📋 Important Information</h3>
          <div className="info-content">
            <h4>What is Third-Party Content?</h4>
            <p>
              Third-party content includes any material (images, music, videos, text, etc.) 
              that was not created by you or your organization. Examples include:
            </p>
            <ul>
              <li>Stock photos or images from image libraries</li>
              <li>Licensed music or sound effects</li>
              <li>Content from content delivery networks (CDNs)</li>
              <li>User-generated content</li>
              <li>Third-party APIs that provide content</li>
            </ul>

            <h4>Which Option Should I Choose?</h4>
            <ul>
              <li>
                <strong>Does Not Use Third-Party Content:</strong> Choose this if all content 
                in your app is original or you own all the rights to it.
              </li>
              <li>
                <strong>Uses Third-Party Content:</strong> Choose this if your app includes 
                any content from external sources, even if you have proper licenses.
              </li>
            </ul>

            <h4>Requirements:</h4>
            <ul>
              <li>This declaration is <strong>required</strong> before submitting your app</li>
              <li>Be honest and accurate in your declaration</li>
              <li>If you use third-party content, ensure you have proper rights/licenses</li>
              <li>Apple may request proof of rights during the review process</li>
              <li>False declarations can result in app rejection or removal</li>
            </ul>

            <h4>Legal Considerations:</h4>
            <div className="warning-box">
              <p>
                <strong>⚠️ Important:</strong> If you declare that your app uses third-party content, 
                you are legally responsible for ensuring you have the proper rights, licenses, 
                or permissions to use that content. Apple may ask for documentation proving 
                your right to use the content.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="resources-section">
          <h3>📚 Additional Resources</h3>
          <div className="resource-links">
            <a 
              href="https://developer.apple.com/app-store/review/guidelines/#intellectual-property" 
              target="_blank" 
              rel="noopener noreferrer"
              className="resource-link"
            >
              📖 App Store Review Guidelines - Intellectual Property
            </a>
            <a 
              href="https://appstoreconnect.apple.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="resource-link"
            >
              🍎 Manage in App Store Connect
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        .content-rights-section {
          padding: 2rem;
          max-width: 900px;
        }

        .section-header {
          margin-bottom: 2rem;
        }

        .section-header h2 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .section-header p {
          color: var(--text-secondary);
          font-size: 1rem;
        }

        .alert {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .alert-error {
          background-color: var(--error-bg, #fff5f5);
          border: 1px solid var(--error-border, #feb2b2);
          color: var(--error-text, #c53030);
        }

        .alert-success {
          background-color: var(--success-bg, #f0fdf4);
          border: 1px solid var(--success-border, #86efac);
          color: var(--success-text, #15803d);
        }

        .loading-spinner {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid var(--border-color);
          border-top-color: var(--accent-color);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .content-rights-content > div {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .current-status-section h3,
        .declaration-options-section h3,
        .info-section h3,
        .resources-section h3 {
          font-size: 1.3rem;
          margin-bottom: 1rem;
          color: var(--text-primary);
        }

        .status-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .status-does-not-use-third-party-content {
          background-color: var(--success-bg, #d1fae5);
          color: var(--success-text, #065f46);
        }

        .status-uses-third-party-content {
          background-color: var(--warning-bg, #fef3c7);
          color: var(--warning-text, #92400e);
        }

        .status-not-set {
          background-color: var(--error-bg, #fee2e2);
          color: var(--error-text, #991b1b);
        }

        .declaration-options-section p {
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }

        .radio-options {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .radio-option {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .radio-option:hover {
          border-color: var(--accent-color);
          background-color: var(--bg-secondary);
        }

        .radio-option input[type="radio"] {
          margin-top: 0.25rem;
          cursor: pointer;
        }

        .radio-option input[type="radio"]:disabled {
          cursor: not-allowed;
        }

        .radio-content {
          flex: 1;
        }

        .radio-title {
          font-weight: 600;
          font-size: 1.05rem;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .radio-description {
          color: var(--text-secondary);
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-primary {
          background-color: var(--accent-color);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: var(--accent-hover);
        }

        .btn:disabled {
          background-color: var(--border-color);
          color: var(--text-secondary);
          cursor: not-allowed;
        }

        .info-section h4 {
          font-size: 1.1rem;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: var(--text-primary);
        }

        .info-section h4:first-of-type {
          margin-top: 0;
        }

        .info-section p,
        .info-section ul {
          color: var(--text-primary);
          line-height: 1.6;
          margin-bottom: 1rem;
        }

        .info-section ul {
          padding-left: 1.5rem;
        }

        .info-section li {
          margin-bottom: 0.5rem;
        }

        .warning-box {
          background-color: var(--warning-bg, #fff7ed);
          border: 2px solid var(--warning-border, #fb923c);
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .warning-box p {
          margin: 0;
          color: var(--warning-text, #7c2d12);
        }

        .resources-section {
          background: var(--bg-secondary) !important;
          border: none !important;
        }

        .resource-links {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .resource-link {
          display: block;
          padding: 0.75rem 1rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--accent-color);
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .resource-link:hover {
          background: var(--accent-color);
          color: white;
          transform: translateX(4px);
        }
      `}</style>
    </div>
  );
}

export default ContentRights;
